<template>
  <v-dialog :model-value="modelValue" max-width="640" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <div v-if="profile && existing" class="pdlg" role="dialog" aria-modal="true" aria-labelledby="merge-h">
      <div class="pdlg-head">
        <h2 id="merge-h">Is this the same provider?</h2>
        <button type="button" class="pdlg-x" aria-label="Close" :disabled="saving" @click="keep">&#10005;</button>
      </div>
      <div class="pdlg-body">
        <p class="sp-lead">This NPI is already on <strong>Dr. {{ existing.name }}</strong><template v-if="existingAuditName"> in your <strong>{{ existingAuditName }}</strong> audit</template>.</p>
        <div class="sp-cards">
          <div class="sp-card">
            <span class="sp-tag mine">This audit</span>
            <strong>Dr. {{ profile.name }}</strong>
            <span class="sp-sub">{{ auditName }}</span>
            <span class="sp-sub">Added by name · No NPI yet</span>
          </div>
          <div class="sp-card">
            <span class="sp-tag acct">Already in your account</span>
            <strong>Dr. {{ existing.name }}</strong>
            <span v-if="existingAuditName" class="sp-sub">{{ existingAuditName }}</span>
            <span class="sp-sub">{{ ['NPI ' + existing.npi, existing.specialty].filter(Boolean).join(' · ') }}</span>
          </div>
        </div>
        <div class="sp-list">
          <strong>If you link them</strong>
          <span><span aria-hidden="true" class="sp-tick">&#10003;</span>One provider profile, shown in both audits.</span>
          <span><span aria-hidden="true" class="sp-tick">&#10003;</span>Credentials and specialty come from the NPI Registry.</span>
          <span><span aria-hidden="true" class="sp-tick">&#10003;</span>Each audit keeps its own results. Nothing gets mixed or added up.</span>
        </div>
        <div class="sp-note">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a5568" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
          <span>Not the same person? One NPI can only belong to one provider. Pick a different NPI, or keep this provider without one. The NPI stays on Dr. {{ existing.name }}<template v-if="existingAuditName"> in {{ existingAuditName }}</template>, so check that provider if it looks wrong.</span>
        </div>
        <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>
      </div>
      <div class="pdlg-foot split">
        <button type="button" class="pdlg-link lg" :disabled="saving" @click="different">&larr; Pick a different NPI</button>
        <div class="pdlg-btns">
          <button type="button" class="pdlg-btn" :disabled="saving" @click="keep">Keep without an NPI</button>
          <button type="button" class="pdlg-btn primary" :disabled="saving" @click="link">{{ saving ? 'Linking…' : 'Yes, link them' }}</button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mergeProviders } from './providersApi'
import { track } from './track'
import { errorMessage } from './providerErrors'

export default {
  name: 'SameProviderDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    reviewEntityId: { type: [Number, String], default: null },
    profile: { type: Object, default: null },
    existing: { type: Object, default: null },
    auditName: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'different-npi', 'keep', 'linked'],
  data() {
    return { saving: false, error: '' }
  },
  computed: {
    existingAuditName() {
      return this.existing?.audits?.[0]?.name || ''
    }
  },
  watch: {
    modelValue(open) {
      if (open) Object.assign(this, { saving: false, error: '' })
    }
  },
  methods: {
    different() {
      track('provider_merge_choice', { choice: 'different_npi' })
      this.$emit('different-npi')
    },
    keep() {
      track('provider_merge_choice', { choice: 'keep' })
      this.$emit('keep')
    },
    async link() {
      if (this.saving) return
      this.saving = true
      this.error = ''
      try {
        const res = await mergeProviders(this.reviewEntityId, this.profile.id, this.existing.id)
        track('provider_merge_choice', { choice: 'link' })
        this.$emit('linked', res.provider)
      } catch (e) {
        this.error = errorMessage(e, "We couldn't link these providers. Try again.")
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped src="./dialogStyles.css"></style>
<style scoped>
.pdlg { background: #fff; border-radius: 14px; display: flex; flex-direction: column; max-height: calc(100vh - 48px); overflow-y: auto; box-shadow: 0 16px 48px rgba(13, 30, 53, 0.25); }
.pdlg-body { gap: 16px; }
.sp-lead { margin: 0; }
.sp-cards { display: flex; gap: 12px; flex-wrap: wrap; }
.sp-card { flex: 1 1 240px; min-width: 0; border: 1px solid #e6e1d4; border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; gap: 6px; }
.sp-card strong { font-size: 16px; }
.sp-sub { font-size: 13px; color: #4a5568; }
.sp-tag { align-self: flex-start; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; }
.sp-tag.mine { background: #f7f4ec; color: #5b4a1f; }
.sp-tag.acct { background: #daeefa; color: #154b7a; }
.sp-list { display: flex; flex-direction: column; gap: 8px; background: #f9f7f1; border-radius: 10px; padding: 14px 16px; font-size: 14px; }
.sp-list > span { display: flex; gap: 8px; }
.sp-tick { color: #0e7c80; font-weight: 700; }
.sp-note { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: #33465e; }
.sp-note svg { flex: 0 0 auto; margin-top: 1px; }
</style>
